import { Chart, RectangleStackIcon, SimpleGrid } from 'components'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { flatten, uniq } from 'lodash-es'
import { ChartProps } from 'types'
import { useUsers } from 'utils'
import { StatTile } from '..'
import { useEvents } from '../../utils'
import { useAggregations } from '../../utils/analyticsUtils'
import { useWorkspaceAnalytics } from './WorkspaceAnalyticsProvider'

dayjs.extend(isBetween)

export default function WorkspaceAnalyticsEventsSection() {
  const { dateRange } = useWorkspaceAnalytics()
  const { events } = useEvents()

  let filteredEvents = events
  if (dateRange?.from && dateRange?.to) {
    const { from, to } = dateRange
    filteredEvents = events?.filter(e => {
      const startBetween = dayjs(e.startTime).isBetween(from, to, null, '[]')
      const endBetween = dayjs(e.endTime).isBetween(from, to, null, '[]')
      return startBetween || endBetween
    })
  }
  const eventMemberIds =
    (flatten(filteredEvents?.map(event => event.memberIds) || []).filter(Boolean) as string[]) || []
  const uniqEventMemberIds = uniq(eventMemberIds)
  const { data: eventUsers } = useUsers(uniqEventMemberIds)
  const { aggregations } = useAggregations(
    [
      {
        table: 'leads',
        values: ['_count'],
        categories: ['followUpGroup'],
        sort: { _count: 'DESC' },
        filters: [{ field: 'followUpGroup', operator: 'isNull', value: false, table: 'leads' }],
      },
      {
        table: 'leads',
        values: ['_count'],
        categories: ['followUpUserId'],
        sort: { _count: 'DESC' },
        filters: [{ field: 'followUpUserId', operator: 'isNull', value: false, table: 'leads' }],
      },
      {
        table: 'leadSessions',
        values: ['_count'],
        categories: ['eventId'],
        sort: { _count: 'DESC' },
      },
    ],
    { dateRange }
  )

  const [ownerGroups, followUpUserIds, sessionsByEvent] = aggregations || {}
  const { data: followUpUsers } = useUsers(
    followUpUserIds?.data?.map(u => u.followUpUserId as string) || []
  )
  const groupData =
    ownerGroups?.data?.map(u => ({ owner: u.followUpGroup, _count: u._count })) || []
  const userData =
    followUpUserIds?.data?.map(u => ({
      owner: followUpUsers.find(user => user?.id === u.followUpUserId)?.name || u.followUpUserId,
      _count: u._count,
    })) || []
  const owners: ChartProps = {
    categories: ['owner'],
    values: ['_count'],
    data: [...groupData, ...userData],
  }

  const eventsByMemberData =
    eventMemberIds?.reduce(
      (acc, cur) => {
        const userName = eventUsers?.find(user => user?.id === cur)?.name || cur
        const index = acc.findIndex(item => item.userName === userName)
        if (index > -1) acc[index].eventCount += 1
        else acc.push({ userName, eventCount: 1 })
        return acc
      },
      [] as { userName: string; eventCount: number }[]
    ) ?? []
  eventsByMemberData.sort((a, b) => b.eventCount - a.eventCount)
  const eventsByMember: ChartProps = {
    data: eventsByMemberData || [],
    categories: ['userName'],
    values: ['eventCount'],
  }

  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
      {!!owners.data?.length && (
        <StatTile
          alignItems="stretch"
          height="400px"
          label="Leads by Owner"
          num={owners.data?.length}
          Icon={RectangleStackIcon}
          canCopy
        >
          <Chart {...owners} />
        </StatTile>
      )}

      {!!eventsByMemberData.length && (
        <StatTile
          alignItems="stretch"
          height="400px"
          label="Event Member Attendance"
          Icon={RectangleStackIcon}
          canCopy
        >
          <Chart
            {...eventsByMember}
            categoryInfo={[{ formatter: v => events?.find(e => e.id === v)?.title || v }]}
          />
        </StatTile>
      )}

      <StatTile
        alignItems="stretch"
        height="400px"
        label="Sessions by Event"
        num={sessionsByEvent?.data?.length}
        Icon={RectangleStackIcon}
        canCopy
      >
        <Chart
          {...sessionsByEvent}
          categoryInfo={[{ formatter: v => events?.find(e => e.id === v)?.title || v }]}
        />
      </StatTile>
    </SimpleGrid>
  )
}
